/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Box,
  VStack,
  HStack,
  Text,
  Textarea,
  Button,
  useToast,
  Spinner,
  IconButton,
  Badge,
  Divider,
  Icon,
  Flex,
} from "@chakra-ui/react";
import {
  FiSend,
  FiSave,
  FiTrash2,
  FiEdit2,
  FiRefreshCw,
  FiMessageSquare,
} from "react-icons/fi";
import api from "utils/api";

const HatirlatmaGonderModal = ({ isOpen, onClose, abonelik }) => {
  const toast = useToast();
  const [message, setMessage] = useState("");
  const [savedMessages, setSavedMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const deviceId = abonelik?.cihaz_id || abonelik?.device_id;

  // Kayıtlı mesajları yükle
  const loadSavedMessages = async () => {
    setLoadingMessages(true);
    try {
      const res = await api.get("/api/abonelikler/sms/saved");
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Mesajlar yüklenemedi");
      }
      setSavedMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      toast({
        title: "Hata",
        description: err.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadSavedMessages();
      setMessage("");
      setEditingId(null);
      setEditText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // SMS gönder
  const handleSendSms = async (saveMessage = false) => {
    if (!message.trim()) {
      toast({
        title: "Uyarı",
        description: "Lütfen mesaj metni girin",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (!deviceId) {
      toast({
        title: "Hata",
        description: "Araç bilgisi bulunamadı",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/api/abonelikler/sms/send", {
        device_id: deviceId,
        message: message.trim(),
        save_message: saveMessage,
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.detail || "SMS gönderilemedi");
      }

      if (data.success) {
        toast({
          title: "Başarılı",
          description: "SMS başarıyla gönderildi",
          status: "success",
          duration: 3000,
        });

        // Mesaj kaydedildiyse listeyi yenile
        if (saveMessage) {
          loadSavedMessages();
        }

        // Mesajı temizle
        setMessage("");
      } else {
        throw new Error(data.message || "SMS gönderilemedi");
      }
    } catch (err) {
      toast({
        title: "Hata",
        description: err.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Mesajı kaydet
  const handleSaveMessage = async () => {
    if (!message.trim()) {
      toast({
        title: "Uyarı",
        description: "Lütfen mesaj metni girin",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      const res = await api.post("/api/abonelikler/sms/saved", {
        mesaj_metni: message.trim(),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.detail || "Mesaj kaydedilemedi");
      }

      toast({
        title: "Başarılı",
        description: "Mesaj kaydedildi",
        status: "success",
        duration: 2000,
      });

      loadSavedMessages();
    } catch (err) {
      toast({
        title: "Hata",
        description: err.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  // Mesajı sil
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("Bu mesajı silmek istediğinize emin misiniz?")) {
      return;
    }

    try {
      const res = await api.delete(`/api/abonelikler/sms/saved/${messageId}`);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data?.detail || "Mesaj silinemedi");
      }

      toast({
        title: "Başarılı",
        description: "Mesaj silindi",
        status: "success",
        duration: 2000,
      });

      loadSavedMessages();
    } catch (err) {
      toast({
        title: "Hata",
        description: err.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  // Mesajı düzenle
  const handleStartEdit = (msg) => {
    setEditingId(msg.id);
    setEditText(msg.mesaj_metni);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const handleSaveEdit = async (messageId) => {
    if (!editText.trim()) {
      toast({
        title: "Uyarı",
        description: "Mesaj metni boş olamaz",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      const res = await api.put(`/api/abonelikler/sms/saved/${messageId}`, {
        mesaj_metni: editText.trim(),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.detail || "Mesaj güncellenemedi");
      }

      toast({
        title: "Başarılı",
        description: "Mesaj güncellendi",
        status: "success",
        duration: 2000,
      });

      setEditingId(null);
      setEditText("");
      loadSavedMessages();
    } catch (err) {
      toast({
        title: "Hata",
        description: err.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  // Kayıtlı mesajı seç
  const handleSelectMessage = (msg) => {
    setMessage(msg.mesaj_metni);
  };

  if (!abonelik) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" overflow="hidden" bg="white" p={1}>
        {/* Header */}
        <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200">
          <ModalCloseButton size="sm" />
          <VStack spacing={1} align="start">
            <Text fontSize="lg" fontWeight="medium" color="gray.800">
              Hatırlatma SMS Gönder
            </Text>
            <Text fontSize="xs" color="gray.600">
              {abonelik.plaka || "-"} • {abonelik.firma || "-"}
            </Text>
          </VStack>
        </Box>

        <ModalBody px={6} py={4} maxH="600px" overflowY="auto">
          <VStack align="stretch" spacing={4}>
            {/* Mesaj Yazma Alanı */}
            <Box
              bg="white"
              borderRadius="xl"
              shadow="sm"
              p={4}
              border="1px solid"
              borderColor="gray.100"
            >
              <HStack justify="space-between" mb={3}>
                <HStack spacing={2}>
                  <Icon as={FiMessageSquare} />
                  <Text fontWeight="semibold">Mesaj Metni</Text>
                </HStack>
              </HStack>

              <Textarea
                variant="flushed"
                size="sm"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Hatırlatma mesajınızı buraya yazın..."
                rows={6}
                resize="vertical"
              />

              <HStack mt={3} spacing={2}>
                <Button
                  leftIcon={<FiSend />}
                  colorScheme="blue"
                  onClick={() => handleSendSms(false)}
                  isLoading={loading}
                  loadingText="Gönderiliyor..."
                  size="sm"
                >
                  Gönder
                </Button>
                <Button
                  leftIcon={<FiSend />}
                  colorScheme="green"
                  variant="outline"
                  onClick={() => handleSendSms(true)}
                  isLoading={loading}
                  loadingText="Gönderiliyor..."
                  size="sm"
                >
                  Gönder ve Kaydet
                </Button>
                <Button
                  leftIcon={<FiSave />}
                  variant="outline"
                  onClick={handleSaveMessage}
                  size="sm"
                >
                  Sadece Kaydet
                </Button>
              </HStack>
            </Box>

            {/* Kayıtlı Mesajlar */}
            <Box
              bg="white"
              borderRadius="xl"
              shadow="sm"
              p={4}
              border="1px solid"
              borderColor="gray.100"
            >
              <HStack justify="space-between" mb={3}>
                <HStack spacing={2}>
                  <Icon as={FiMessageSquare} />
                  <Text fontWeight="semibold">Kayıtlı Mesajlar</Text>
                </HStack>
                <IconButton
                  icon={<FiRefreshCw />}
                  size="xs"
                  onClick={loadSavedMessages}
                  isLoading={loadingMessages}
                  aria-label="Yenile"
                />
              </HStack>

              {loadingMessages ? (
                <VStack py={6}>
                  <Spinner />
                  <Text fontSize="sm" color="gray.500">
                    Yükleniyor...
                  </Text>
                </VStack>
              ) : savedMessages.length === 0 ? (
                <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                  Henüz kayıtlı mesaj yok
                </Text>
              ) : (
                <VStack align="stretch" spacing={2} maxH="300px" overflowY="auto">
                  {savedMessages.map((msg) => (
                    <Box
                      key={msg.id}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={3}
                      bg="gray.50"
                      _hover={{ bg: "gray.100" }}
                    >
                      {editingId === msg.id ? (
                        <VStack align="stretch" spacing={2}>
                          <Textarea
                            variant="flushed"
                            size="sm"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            rows={3}
                            resize="vertical"
                          />
                          <HStack justify="flex-end" spacing={2}>
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={handleCancelEdit}
                            >
                              İptal
                            </Button>
                            <Button
                              size="xs"
                              colorScheme="blue"
                              onClick={() => handleSaveEdit(msg.id)}
                            >
                              Kaydet
                            </Button>
                          </HStack>
                        </VStack>
                      ) : (
                        <>
                          <Flex justify="space-between" align="flex-start" mb={2}>
                            <Text fontSize="sm" flex="1" whiteSpace="pre-wrap">
                              {msg.mesaj_metni}
                            </Text>
                            <HStack spacing={1} ml={2}>
                              <IconButton
                                icon={<FiEdit2 />}
                                size="xs"
                                variant="ghost"
                                onClick={() => handleStartEdit(msg)}
                                aria-label="Düzenle"
                              />
                              <IconButton
                                icon={<FiTrash2 />}
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => handleDeleteMessage(msg.id)}
                                aria-label="Sil"
                              />
                            </HStack>
                          </Flex>
                          <Divider my={2} />
                          <HStack justify="space-between">
                            <HStack spacing={2}>
                              <Button
                                size="xs"
                                leftIcon={<FiSend />}
                                colorScheme="blue"
                                variant="outline"
                                onClick={() => handleSelectMessage(msg)}
                              >
                                Seç
                              </Button>
                              <Badge colorScheme="gray" fontSize="xs">
                                {msg.kullanilma_sayisi} kez kullanıldı
                              </Badge>
                            </HStack>
                            <Text fontSize="xs" color="gray.500">
                              {new Date(msg.olusturulma_tarihi).toLocaleDateString(
                                "tr-TR"
                              )}
                            </Text>
                          </HStack>
                        </>
                      )}
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter bg="white" px={6} py={4}>
          <Button variant="ghost" onClick={onClose} size="sm">
            Kapat
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default HatirlatmaGonderModal;

